import { Injectable } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ConfigurationService, TranslationService } from '..';
import { Aliases } from '../models/aliases';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Injectable({
  providedIn: 'root',
})
export class InpageAliasService {
  constructor(
    private configurationService: ConfigurationService,
    private translationService: TranslationService,
    private titleCasePipe: TitleCasePipe
  ) {}

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  async get(aliasKey: string) {
    const userInfo =
      await this.configurationService.getAdditionalUserInformation();
    const alias : Aliases = this.getAliasLabel(aliasKey, userInfo.configFeatures.aliases);
    const translatedValue = this.translationService.getTranslationForKeyClientCustomization(alias.isOverride ? alias.key : alias.value, alias.value, this.translationRequest, true);
    return translatedValue;
  }

  private isVowel(char: string): boolean {
    return ['a', 'e', 'i', 'o', 'u'].includes(char.toLowerCase());
  }

  private getAliasLabel(input: string, aliases: any[]): Aliases {
    const alias = aliases.find(
      (a) => a.key?.toLowerCase() === input?.toLowerCase()
    );
    return alias; //alias?.value ?? null;
  }

  private async getAlias(input: string, aliases: any[]): Promise<Aliases> {
    const alias = aliases.find(
      (a) => (a.key ?? a.Key)?.toLowerCase() === input?.toLowerCase()
    );

    if (!alias) {
      return { value: input, key: input, isOverride: false };
    }

    const translatedValue = await this.get(alias.key ?? alias.Key);

    return {
      value: translatedValue,
      key: alias.key ?? alias.Key,
      isOverride: alias.isOverride ?? alias.IsOverride ?? false,
    };
  }

  public async replaceAliases(input: string, aliases: any[]): Promise<Aliases> {
    return await this.getAlias(input, aliases);
  }

  public replaceAliasesInSentence(input: string, aliases: any[]): string {
    const alias = this.getAliasLabel(input, aliases);
    const article = this.isVowel(alias?.value?.charAt(0)) ? 'an' : 'a';
    return article + ' ' + (alias ?? '');
  }

  /**
   * Returns the alias value with optional case transformation and translation,
   * respecting IsOverride (overridden values are returned unchanged).
   *
   * @param alias The alias object
   * @param caseModifier 'none' | 'titlecase' | 'sentencecase' | 'uppercase' | 'lowercase'
   * @param translationRequest Optional translation request for non-overridden values
   */
  public getTransformedValue(
    alias: Aliases,
    caseModifier: string = 'none',
  ): string {
    if (!alias?.value) {
      return '';
    }

    if (alias.isOverride) {
      return alias.value;
    }

    let value = alias.value;

    switch (caseModifier) {
      case 'titlecase':
        value = this.titleCasePipe.transform(value);
        break;
      case 'sentencecase':
        value = this.toSentenceCase(value);
        break;
      case 'uppercase':
        value = value.toUpperCase();
        break;
      case 'lowercase':
        value = value.toLowerCase();
        break;
    }

    return value;
  }

  /**
   * Builds a sentence from interleaved text fragments and alias values.
   * Plain strings are translated; Aliases respect IsOverride and apply the given case modifier.
   *
   * Each element is either:
   *   - a plain string (translated automatically)
   *   - an object { alias, caseModifier? } (override-aware, optionally case-transformed and translated)
   *
   * Example:
   *   composeSentence([
   *     'We have forwarded this to your',
   *     { alias: this.examinerLabel },
   *     'to review and process.',
   *   ], this.translationRequest);
   */
  public composeSentence(
    parts: Array<string | { alias: Aliases; caseModifier?: string }>,
    translationRequest: TranslationRequest = null
  ): string {
    return parts
      .map((part) => {
        if (typeof part === 'string') {
          return translationRequest
            ? this.translationService.getTranslationForKey(part, translationRequest)
            : part;
        }
        return this.getTransformedValue(
          part.alias,
          part.caseModifier ?? 'none',
        );
      })
      .join(' ');
  }

  private toSentenceCase(input: string): string {
    if (!input || input.length < 1) {
      return input;
    }
    const words = input.split(' ');
    let firstProcessed = false;
    return words
      .map((word) => {
        if (/\d/.test(word)) {
          return word;
        }
        if (!firstProcessed) {
          firstProcessed = true;
          return this.titleCasePipe.transform(word);
        }
        return word.toLowerCase();
      })
      .join(' ');
  }
}
